import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Table, Button, Space, Input, Select, Tooltip } from 'antd';
import { EditOutlined, EyeOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { columnLabels, STATUSES } from '../../constants/taskColumnLabels';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import StatusTag from '../StatusTag';
import { canEditTask } from '../../utils/taskPermissions';

const TaskTable = ({
  tasks,
  onView,
  onEdit,
  onDelete,
  refreshFlag,
  visibleColumns,
  candidates,
  users,
  pageSize = 10,
  showSizeChanger = true,
  showQuickJumper = true,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  filters: externalFilters,
  onFiltersChange,
  isModalMode = false,
}) => {
  const [loading] = useState(false);
  const [localFilters, setLocalFilters] = useState({});
  const filters = externalFilters !== undefined ? externalFilters : localFilters;
  const setFilters = onFiltersChange || setLocalFilters;
  const [tempFilters, setTempFilters] = useState({});
  const [displayedCount, setDisplayedCount] = useState(10);
  const [modalFilters, setModalFilters] = useState({});
  const userRole = useSelector((state) => state.auth.role);
  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = userRole || storedRole;

  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (isModalMode) {
      setDisplayedCount(10);
      setModalFilters({});
    }
  }, [tasks, isModalMode]);

  const handleShowMore = useCallback(() => {
    setDisplayedCount(prev => prev + 10);
  }, []);

  // Применяем фильтры к данным в модальном режиме
  const filteredTasks = useMemo(() => {
    if (!isModalMode) return tasks;

    let filtered = Array.isArray(tasks) ? tasks : [];

    // Применяем фильтры из modalFilters
    Object.keys(modalFilters).forEach(key => {
      const filterValue = modalFilters[key];
      if (filterValue !== null && filterValue !== undefined && filterValue !== '') {
        filtered = filtered.filter(record => {
          if (key === 'status') {
            if (Array.isArray(filterValue)) {
              return filterValue.includes(record[key]);
            }
            return String(record[key]) === String(filterValue);
          }
          if (key === 'deadline' || key === 'updated_at') {
            // Для дат форматируем значение для поиска
            const dateValue = record[key];
            if (!dateValue) return false;
            const formattedDate = new Date(dateValue).toLocaleDateString('ru-RU');
            return formattedDate.toLowerCase().includes(String(filterValue).toLowerCase());
          }
          const cellValue = record[key];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase());
        });
      }
    });

    return filtered;
  }, [isModalMode, tasks, modalFilters]);

  const displayedTasks = isModalMode
    ? (Array.isArray(filteredTasks) ? filteredTasks.slice(0, displayedCount) : [])
    : (Array.isArray(tasks) ? tasks : []);

  const hasMoreTasks = isModalMode && Array.isArray(filteredTasks) && displayedCount < filteredTasks.length;

  const location = useLocation();
  const isCandidatesPage = location.pathname.includes('/tasks');
  const navigate = useNavigate();

  const getEntityName = useCallback((id, entities) => {
    if (!entities || !Array.isArray(entities)) return 'Не указан';
    const entity = entities.find(e => e.id === id);
    return entity ? entity.full_name || `${entity.first_name} ${entity.last_name}` : 'Не указан';
  }, []);

  const getVacancyName = useCallback((task) => {
    // Сначала проверяем новое поле vacancy_name
    if (task.vacancy_name) {
      return task.vacancy_name;
    }
    // Затем проверяем вложенный объект vacancy (старый формат)
    if (task.vacancy && task.vacancy.job_title) {
      return task.vacancy.job_title;
    }
    return 'Не указано';
  }, []);

  const handleTableChange = (pagination, filters, sorter) => {
    const newTempFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key].length > 0) {
        newTempFilters[key] = key === 'status' ? filters[key] : filters[key][0];
      }
    });
    setTempFilters(newTempFilters);
  };

  const handleApplyFilters = useCallback(() => {
    setFilters(tempFilters);
  }, [tempFilters, setFilters]);

  const handleResetFilters = useCallback(() => {
    setTempFilters({});
    setFilters({});
  }, [setFilters]);

  const hasTempFiltersForTitle = Object.keys(tempFilters).length > 0;


  // Создаем опции для селекта статусов из констант
  const statusOptions = Object.entries(STATUSES).map(([key, value]) => ({
    value: key,
    label: value.title
  }));

  const generateColumns = useMemo(() => {
    if (!visibleColumns) return [];

    const columnRenderMap = {
      candidate_id: (id, record) => {
        // Кандидат теперь приходит вложенным в задачу
        if (record.candidate && record.candidate.full_name) {
          return record.candidate.full_name;
        }
        return getEntityName(id, candidates);
      },
      description: (text) => text || 'Не указано',
      deadline: (date) => date ? new Date(date).toLocaleDateString() : 'Не указано',
      assignee_id: (id) => getEntityName(id, users),
      status: (status) => <StatusTag status={status} statuses={STATUSES} />,
      co_assignee_id: (id) => getEntityName(id, users),
      observer_id: (id) => getEntityName(id, users),
      creator_id: (id) => getEntityName(id, users),
      vacancy_names: (_, record) => getVacancyName(record)
    };

    const actionColumn = {
      title: isModalMode ? 'Действия' : (
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleApplyFilters}
          disabled={!hasTempFiltersForTitle}
          size="small"
        >
          Найти
        </Button>
      ),
      key: 'actions',
      width: 80,
      style: {
        wordBreak: 'keep-all',
        whiteSpace: 'nowrap'
      },
      onCell: () => ({
        style: {
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap'
        },
        className: 'actions-column'
      }),
      render: (_, task) => {
        const currentUserId = parseInt(localStorage.getItem('user_id'));
        const { canEdit, reason } = canEditTask(task.creator_id, currentUserId, effectiveRole, task);

        return (
          isCandidatesPage ? (
            <Space size="small">
              <Tooltip title="Просмотреть задачу">
                <Button icon={<EyeOutlined />} size="small" onClick={() => navigate(`/tasks/${task.id}`)} />
              </Tooltip>
              <Tooltip title={canEdit ? "Редактировать задачу" : reason}>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  disabled={!canEdit}
                  onClick={() => onEdit(task)}
                />
              </Tooltip>
            </Space >
          ) : (
            // <Link to={`/tasks?openTask=${task.id}`}></Link>
            <Link to={`/tasks/${task.id}`}>
              Перейти
            </Link>
          )
        );
      }
    };

    const baseCols = visibleColumns
      .filter(key => key !== 'actions' && key && columnLabels[key])
      .map((key) => ({
        title: columnLabels[key],
        dataIndex: key,
        key,
        render: (text, record) => {
          if (columnRenderMap[key]) {
            return columnRenderMap[key](text, record);
          }
          return text;
        },
        ellipsis: false,
        width: key === 'id' ? 80 : undefined,
        style: {
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          maxWidth: key === 'id' ? 80 : 200
        },
        onCell: () => ({
          style: {
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            maxWidth: key === 'id' ? 80 : 200
          }
        }),
        filteredValue: isModalMode
          ? (modalFilters[key] !== undefined ? (key === 'status' && Array.isArray(modalFilters[key]) ? modalFilters[key] : [modalFilters[key]]) : null)
          : (tempFilters[key] ? (key === 'status' ? (Array.isArray(tempFilters[key]) ? tempFilters[key] : [tempFilters[key]]) : [tempFilters[key]]) : null),
        sorter: key === 'id' ? (a, b) => a.id - b.id : undefined,
        onFilter: isModalMode ? (value, record) => {
          if (key === 'status') {
            if (Array.isArray(value)) {
              return value.includes(record[key]);
            }
            return String(record[key]) === String(value);
          }
          if (key === 'deadline' || key === 'updated_at') {
            // Для дат форматируем значение для поиска
            const dateValue = record[key];
            if (!dateValue) return false;
            const formattedDate = new Date(dateValue).toLocaleDateString('ru-RU');
            return formattedDate.toLowerCase().includes(String(value).toLowerCase());
          }
          const cellValue = record[key];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(String(value).toLowerCase());
        } : false,
        filters: isModalMode ? (() => {
          if (key === 'status') {
            // Собираем уникальные статусы из записей
            const statusSet = new Set();
            tasks.forEach(task => {
              if (task[key]) {
                statusSet.add(task[key]);
              }
            });
            return Array.from(statusSet).map(statusValue => {
              const statusOpt = statusOptions.find(opt => opt.value === statusValue);
              return {
                text: statusOpt ? statusOpt.label : statusValue,
                value: statusValue
              };
            });
          }
          return undefined;
        })() : undefined,
        filterDropdown: isModalMode ? (key === 'status' ? undefined : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
          return (
            <div style={{ padding: 8 }}>
              <Input
                placeholder={`Поиск в ${columnLabels[key]}`}
                value={selectedKeys?.[0] || modalFilters[key] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedKeys(value ? [value] : []);
                }}
                onPressEnter={() => {
                  const filterValue = selectedKeys?.[0] || '';
                  setModalFilters(prev => ({
                    ...prev,
                    [key]: filterValue || null
                  }));
                  confirm();
                }}
                style={{ width: 188, marginBottom: 8, display: 'block' }}
                allowClear
                onClear={() => {
                  setSelectedKeys([]);
                  setModalFilters(prev => {
                    const newFilters = { ...prev };
                    delete newFilters[key];
                    return newFilters;
                  });
                  clearFilters();
                  confirm();
                }}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={() => {
                    const filterValue = selectedKeys?.[0] || '';
                    setModalFilters(prev => ({
                      ...prev,
                      [key]: filterValue || null
                    }));
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Применить
                </Button>
                <Button
                  onClick={() => {
                    setSelectedKeys([]);
                    setModalFilters(prev => {
                      const newFilters = { ...prev };
                      delete newFilters[key];
                      return newFilters;
                    });
                    clearFilters();
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Сброс
                </Button>
              </Space>
            </div>
          );
        }) : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
          const handleApply = () => {
            const newTempFilters = { ...tempFilters };
            if (selectedKeys && selectedKeys.length > 0) {
              if (key === 'status') {
                newTempFilters[key] = selectedKeys;
              } else {
                newTempFilters[key] = selectedKeys[0];
              }
            } else {
              delete newTempFilters[key];
            }
            setTempFilters(newTempFilters);
            confirm();
          };

          const handleClear = () => {
            const newTempFilters = { ...tempFilters };
            delete newTempFilters[key];
            setTempFilters(newTempFilters);
            clearFilters();
            // Если это был последний фильтр, автоматически сбрасываем все фильтры
            if (Object.keys(newTempFilters).length === 0) {
              handleResetFilters();
            }
            confirm();
          };

          if (key === 'status') {
            return (
              <div style={{ padding: 8 }}>
                <Select
                  placeholder="Выберите статусы"
                  value={selectedKeys}
                  onChange={(value) => setSelectedKeys(value || [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  allowClear
                  mode="multiple"
                  options={statusOptions}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          return (
            <div style={{ padding: 8 }}>
              <Input
                placeholder={`Поиск в ${columnLabels[key]}`}
                value={selectedKeys[0]}
                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                style={{ width: 188, marginBottom: 8, display: 'block' }}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={handleApply}
                  size="small"
                  style={{ width: 90 }}
                >
                  Применить
                </Button>
                <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                  Сброс
                </Button>
              </Space>
            </div>
          );
        },
        filterIcon: (filtered) => {
          if (isModalMode) {
            const hasFilter = modalFilters[key] !== undefined && modalFilters[key] !== null && modalFilters[key] !== '';
            return <SearchOutlined style={{ color: (filtered || hasFilter) ? '#1890ff' : undefined }} />;
          }
          const hasTempFilter = tempFilters[key] !== undefined && tempFilters[key] !== null && tempFilters[key] !== '';
          return <SearchOutlined style={{ color: hasTempFilter ? '#1890ff' : undefined }} />;
        },
      }));

    const includeActions = visibleColumns.includes('actions');
    return includeActions ? [...baseCols, actionColumn] : baseCols;
  }, [visibleColumns, candidates, users, tempFilters, modalFilters, isCandidatesPage, onEdit, getEntityName, getVacancyName, statusOptions, effectiveRole, navigate, handleApplyFilters, hasTempFiltersForTitle, isModalMode, tasks]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <div style={{
      '--table-cell-word-break': 'break-word',
      '--table-cell-white-space': 'normal'
    }}>
      {!isModalMode && hasActiveFilters && (
        <div style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '8px 0'
        }}>
          <Button
            icon={<ClearOutlined />}
            onClick={handleResetFilters}
            size="middle"
          >
            Сбросить фильтры
          </Button>
        </div>
      )}
      {isModalMode && (
        <div style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 0'
        }}>
          <span style={{ color: '#666' }}>
            Показано {displayedTasks.length} из {filteredTasks.length} записей {Object.keys(modalFilters).length > 0 && `(всего: ${tasks.length})`}
          </span>
          {hasMoreTasks && (
            <Button
              type="primary"
              onClick={handleShowMore}
            >
              Показать еще 10
            </Button>
          )}
        </div>
      )}
      <Table
        columns={generateColumns || []}
        dataSource={displayedTasks}
        rowKey="id"
        bordered
        size="middle"
        locale={{
          triggerDesc: 'Нажмите для сортировки по убыванию',
          triggerAsc: 'Нажмите для сортировки по возрастанию',
          cancelSort: 'Сбросить сортировку',
        }}
        pagination={isModalMode ? false : (currentPage && totalPages !== undefined && onPageChange ? {
          current: currentPage,
          pageSize: pageSize,
          total: totalItems !== undefined && totalItems !== null ? totalItems : (totalPages * pageSize),
          showTotal: (total, range) => {
            const actualTotal = totalItems !== undefined && totalItems !== null ? totalItems : total;
            const endRange = Math.min(range[1], actualTotal);
            return `${range[0]}-${endRange} из ${actualTotal} записей`;
          },
          showSizeChanger: showSizeChanger,
          showQuickJumper: false,
          showLessItems: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, size) => {
            const newSize = size !== undefined ? size : pageSize;
            onPageChange(page, newSize);
          },
          onShowSizeChange: (current, size) => {
            onPageChange(1, size);
          },
        } : {
          pageSize: pageSize,
          showTotal: (total, range) => {
            const actualTotal = tasks.length;
            const endRange = Math.min(range[1], actualTotal);
            return `${range[0]}-${endRange} из ${actualTotal} записей`;
          },
          showQuickJumper: false,
          defaultPageSize: pageSize
        })}
        loading={loading}
        scroll={{ x: 'max-content' }}
        onChange={isModalMode ? (pagination, tableFilters, sorter) => {
          // Обновляем modalFilters при изменении фильтров через filters (статус)
          const newModalFilters = { ...modalFilters };
          Object.keys(tableFilters).forEach(filterKey => {
            if (tableFilters[filterKey] && tableFilters[filterKey].length > 0) {
              if (filterKey === 'status') {
                newModalFilters[filterKey] = tableFilters[filterKey];
              } else {
                newModalFilters[filterKey] = tableFilters[filterKey][0];
              }
            } else {
              delete newModalFilters[filterKey];
            }
          });
          setModalFilters(newModalFilters);
        } : handleTableChange}
        onRow={(task) => ({
          onDoubleClick: () => {
            // if (!location.pathname.includes('/tasks')) {
            //   navigate(`/tasks?openTask=${task.id}`);
            // } else {
            //   onView(task)
            // }
            navigate(`/tasks/${task.id}`);
          },
          style: { cursor: 'pointer' }
        })}
        className="word-wrap-table"
      />
      <style jsx>{`
        .word-wrap-table .ant-table-cell {
          word-break: break-word !important;
          white-space: normal !important;
          vertical-align: top !important;
        }
        .word-wrap-table .ant-table-cell.actions-column {
          word-break: keep-all !important;
          white-space: nowrap !important;
        }
      `}</style>
    </div>
  );
};

export default TaskTable;